import { reactive } from 'vue'
import WorkflowActionService from '@baserow/modules/database/services/workflowAction'
import { notifyIf } from '@baserow/modules/core/utils/error'
import { clone } from '@baserow/modules/core/utils/object'
import {
  BUTTON_FIELD_CLICK_COOLDOWN_MS,
  isButtonFieldRecentlyClicked,
  isButtonFormulaValueTruthy,
  markButtonFieldClicked,
  resolveButtonFormula,
} from '@baserow/modules/database/utils/buttonField'

// Keyed by field and row rather than kept per component. The grid swaps an
// unselected cell for its own component on the first click, and that remount
// would drop a flag held in `data`, taking the loading state and the double
// click guard with it.
const dispatchesInFlight = reactive(new Set())

/**
 * Dispatches a cell's actions on click and runs the ones the backend hands
 * back. Falls back to the field store where the render context provides no
 * `allFieldsInTable` (functional grid cells, cards).
 */
export default {
  data() {
    return {
      // Set while the confirmation dialog is open, and holds what to do if the
      // user says yes. `null` whenever no click is awaiting an answer, which
      // is also how the `cancel` handler tells a real cancellation from the
      // `hidden` event that a confirmation itself triggers.
      buttonFieldPendingClick: null,
    }
  },
  computed: {
    hasWorkflowActions() {
      return this.field.has_workflow_actions === true
    },
    dispatchKey() {
      return `${this.field.id}:${this.row.id}`
    },
    dispatching() {
      return dispatchesInFlight.has(this.dispatchKey)
    },
    /**
     * The table's fields, from whichever of the two shapes the rendering
     * context provides. Every per-row formula resolves against these, so they
     * all see an identical context.
     */
    buttonFieldFields() {
      return this.allFieldsInTable?.length > 0
        ? this.allFieldsInTable
        : this.$store.getters['field/getAll']
    },
    /**
     * Whether this field asks for confirmation at all.
     *
     * Deliberately a field-level question - "is a confirm formula
     * configured?" - and not a per-row one about what that formula resolves
     * to. Were it per-row, a formula that happened to resolve to nothing on
     * some row (an empty referenced cell, say) would make the confirmation
     * step vanish precisely there, and a safety prompt that silently stops
     * appearing on some rows is worse than one that never existed. The
     * resolved value only decides the wording; `buttonFieldConfirmMessage`
     * falls back to a generic question when it comes back empty.
     *
     * Unconfigured (the default) means no prompt, which is what keeps a plain
     * URL button behaving exactly as it always has.
     */
    buttonFieldConfirmEnabled() {
      return !!this.field.confirm_formula?.formula
    },
    buttonFieldConfirmMessage() {
      const resolved = resolveButtonFormula(
        this.$registry,
        this.field.confirm_formula,
        this.row,
        this.buttonFieldFields
      )
      return resolved || this.$t('buttonField.confirmFallbackMessage')
    },
    /**
     * Whether this row's disabled condition says the button is spent.
     *
     * The formula is resolved per row, so the same field can be live on the
     * rows the action hasn't happened to and dead on the ones it has - which
     * is the point: it lets a button be switched off by the very data the
     * workflow writes back ("disable once the invoice id is filled in")
     * rather than by anything hardcoded about a particular integration.
     */
    buttonFieldDisabledForRow() {
      if (!this.field.disabled_formula?.formula) {
        return false
      }
      return isButtonFormulaValueTruthy(
        resolveButtonFormula(
          this.$registry,
          this.field.disabled_formula,
          this.row,
          this.buttonFieldFields
        )
      )
    },
    /**
     * Whether this button was clicked on this row recently enough that the
     * backend's de-duplication lock would still reject another click.
     *
     * This is the piece that closes the window items 1 and 2 can't: on a fresh
     * row, nothing has been written back yet, so `disabled_formula` still says
     * "go ahead" for the several seconds between the click being accepted and
     * the workflow's result reaching the browser.
     */
    buttonFieldRecentlyClicked() {
      // Kept even though upstream's `dispatching` covers the request itself:
      // that clears the moment the dispatch responds, and a
      // `run_automation_workflow` action returns as soon as the workflow is
      // queued, not when it finishes. The window this closes is the one after
      // the response and before the workflow's write reaches the browser.
      return (
        this.hasWorkflowActions &&
        isButtonFieldRecentlyClicked(this.field.id, this.row.id)
      )
    },
    /**
     * The props every button-rendering component should spread onto its
     * `<Button>`. A button with no actions, a broken guard, or a row its
     * `disabled_formula` switches it off for is rendered disabled; otherwise it
     * shows a loading state while its dispatch is in flight.
     */
    buttonFieldRenderProps() {
      // Always a `<button>` now, never an `<a>`: a URL is an `open_url`
      // workflow action the client runs after the dispatch responds, so there
      // is no href to put on the element any more.
      return {
        type: 'secondary',
        size: 'tiny',
        tag: 'button',
        loading: this.dispatching,
        disabled:
          !this.hasWorkflowActions ||
          this.dispatching ||
          this.buttonFieldRecentlyClicked ||
          this.buttonFieldDisabledForRow ||
          // `error` reports a broken `confirm_formula` or `disabled_formula`,
          // and those are exactly the guards standing between the user and a
          // duplicate run. A guard that can no longer be evaluated fails
          // closed.
          !!this.field.error,
      }
    },
  },
  methods: {
    /**
     * Runs the field's actions against this row. The backend rejects a
     * concurrent click for the same field and row, so the local guard only
     * avoids an obvious double fire.
     */
    async dispatchWorkflowActions() {
      if (this.dispatching) {
        return
      }
      // Captured up front so the release below cannot miss it if the cell is
      // handed a different row while the request is in flight.
      const key = this.dispatchKey
      dispatchesInFlight.add(key)
      try {
        // The dispatch takes its own broadcast, so `this.row` can change
        // mid-request. Client actions get the row as it was at click time.
        const clickedRow = clone(this.row)
        const { data } = await WorkflowActionService(this.$client).dispatch(
          this.field.id,
          this.row.id
        )
        // Marked on success only, and deliberately not cleared afterwards. The
        // request settling is not the work finishing: a
        // `run_automation_workflow` action returns as soon as the run is
        // queued, while the run itself takes seconds more to write anything
        // back. `dispatching` clears in `finally`, so without this the button
        // would re-arm for exactly that window.
        markButtonFieldClicked(this.field.id, this.row.id)
        await this.runClientActions(
          data?.client_actions || [],
          clickedRow,
          this.previousActionResults(data)
        )
      } catch (error) {
        if (
          error.handler?.code === 'ERROR_WORKFLOW_ACTION_DISPATCH_IN_PROGRESS'
        ) {
          // The 409 from the (field_id, row_id) lock (ADR 006 section 3). Not a
          // failure and must not be dressed as one: the backend is saying this
          // exact click already happened and its work may still be running. An
          // error toast would suggest the opposite and invite a retry, which is
          // the very thing the lock prevents. Honour the same window locally.
          markButtonFieldClicked(this.field.id, this.row.id)
          await this.$store.dispatch('toast/info', {
            title: this.$t('buttonField.workflowAlreadyRunningTitle'),
            message: this.$t('buttonField.workflowAlreadyRunningMessage'),
          })
          return
        }
        // The shared handler stays quiet on a 429, so a refused click would
        // otherwise look like a click that did nothing.
        if (error.handler?.isTooManyRequests?.()) {
          this.$store.dispatch('toast/error', {
            title: this.$t('buttonField.rateLimitedTitle'),
            message: this.$t('buttonField.rateLimitedMessage'),
          })
        } else if (error.handler) {
          // A handled error already carries its own message. Anything else, a
          // network failure for instance, still needs a toast of its own.
          notifyIf(error, 'workflowAction')
        } else {
          this.$store.dispatch('toast/error', {
            title: this.$t('buttonField.dispatchErrorTitle'),
            message: this.$t('buttonField.dispatchErrorMessage'),
          })
        }
      } finally {
        dispatchesInFlight.delete(key)
      }
    },
    /**
     * What the server side actions returned, for a client action to reference.
     * The result is keyed by field name, so the ids it came with travel with
     * it: the browser has no other way to map a `field_<id>` in a formula onto
     * a row of a table that is not this one.
     */
    previousActionResults(data) {
      return Object.fromEntries(
        (data?.results || []).map((result) => [
          String(result.workflow_action_id),
          {
            data: result.data,
            fieldNames: result.field_names || {},
            order: result.order,
            position: result.position,
          },
        ])
      )
    },
    /**
     * The results of the actions that ran before this one. Client actions run
     * last whatever their place in the list, so without this a reference to an
     * action ordered after them would resolve here while the dispatch would
     * have refused it.
     */
    resultsBefore(previousActionResults, workflowAction) {
      // Two actions can carry the same `order`, which the dispatch then breaks
      // by id, so `position` is what really says which ran first. `order` is
      // the fallback for a backend that sends no position.
      const byPosition =
        workflowAction.position !== undefined &&
        workflowAction.position !== null
      const place = byPosition ? workflowAction.position : workflowAction.order
      if (place === undefined || place === null) {
        return previousActionResults
      }
      return Object.fromEntries(
        Object.entries(previousActionResults).filter(([, result]) => {
          const resultPlace = byPosition ? result.position : result.order
          return resultPlace === undefined || resultPlace === null
            ? true
            : resultPlace < place
        })
      )
    },
    /**
     * Runs the actions the backend hands back for the browser, in the order it
     * returned them. It only sends them when every server side action
     * succeeded, so a failed row action never navigates away.
     */
    async runClientActions(clientActions, row, previousActionResults = {}) {
      const fields =
        this.allFieldsInTable?.length > 0
          ? this.allFieldsInTable
          : this.$store.getters['field/getAll']
      for (const workflowAction of clientActions) {
        const ran = await this.$registry
          .get('databaseWorkflowActionType', workflowAction.type)
          .execute({
            workflowAction,
            applicationContext: {
              row,
              fields,
              previousActionResults: this.resultsBefore(
                previousActionResults,
                workflowAction
              ),
            },
          })
        // An action that could not run stops the ones after it, the way a
        // failed server action stops the sequence. Carrying on would navigate
        // away from the message this one just raised.
        if (ran === false) {
          break
        }
      }
    },
    async onButtonFieldClick(event) {
      // Never the browser's default any more. Every button dispatches its
      // action list; a URL is an `open_url` action the client runs afterwards,
      // so there is nothing to navigate to directly. Stopping propagation
      // matters in contexts (grid cells, cards) where a click on the cell also
      // selects or opens the row.
      event.preventDefault()
      event.stopPropagation()

      if (this.buttonFieldDisabledForRow || this.field.error) {
        // `<Button disabled>` should already have swallowed this, but the
        // guard must not be merely cosmetic.
        return
      }

      if (this.buttonFieldConfirmEnabled) {
        this.buttonFieldPendingClick = { type: 'dispatch' }
        this.$refs.buttonFieldConfirmModal?.show()
        return
      }

      await this.dispatchWorkflowActions()
    },
    /**
     * The user said yes.
     *
     * The navigation case has to happen synchronously inside this handler:
     * it is the click on the dialog's confirm button that makes `window.open`
     * a user-initiated navigation rather than a pop-up for the browser to
     * block.
     */
    onButtonFieldConfirm() {
      const pending = this.buttonFieldPendingClick
      this.buttonFieldPendingClick = null

      if (!pending) {
        return
      }

      this.dispatchWorkflowActions()
    },
    /**
     * The user said no, or dismissed the dialog.
     *
     * Must leave the workflow unstarted - that is the whole contract of a
     * confirmation step. Clearing the pending click is all it takes, since
     * nothing has been sent yet; the guard against acting on an already
     * answered prompt is `buttonFieldPendingClick` being null, which is what
     * makes this safe to receive after a confirmation too (hiding the modal
     * re-emits it).
     */
    onButtonFieldConfirmCancel() {
      this.buttonFieldPendingClick = null
    },
  },
  beforeUnmount() {
    // The dialog is teleported to the body and outlives this component's own
    // DOM, so a component torn down while its confirmation is open (the grid
    // virtualising the cell away on scroll, the row modal closing) would
    // otherwise leave an orphaned prompt on screen wired to nothing.
    this.buttonFieldPendingClick = null
  },
  // Re-exported so components and tests don't have to reach into the util for
  // the one constant that describes this mixin's externally visible timing.
  BUTTON_FIELD_CLICK_COOLDOWN_MS,
}
